import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PaymentMethodSectionProps } from "../../../../interfaces/order-management-props.interface";
import { PaymentMethodTypeEnum } from "src/enums/order.enum";
import PayBillNmiPayment from "./PayBillNmiPayment";

const PAYMENT_OPTIONS: { value: PaymentMethodTypeEnum; label: string }[] = [
  { value: PaymentMethodTypeEnum.CASH, label: "Cash" },
  { value: PaymentMethodTypeEnum.CREDIT_CARD, label: "Card" },
];

/**
 * Custom dropdown matching Timecards / ReportDateRangePicker pattern
 * ([ReportDateRangePicker.tsx](src/features/Reports/components/ReportDateRangePicker.tsx)):
 * bordered trigger, chevron, portaled menu with shadow, selected row bg-[#fef3ef].
 */
function PayBillPaymentMethodSelect({
  value,
  onChange,
}: {
  value: PaymentMethodTypeEnum;
  onChange: (v: PaymentMethodTypeEnum) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const label = PAYMENT_OPTIONS.find((o) => o.value === value)?.label ?? "Cash";

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gap = 8;
      const pad = 10;
      const vw = window.innerWidth;
      let left = r.left;
      const w = r.width;
      if (left + w > vw - pad) left = Math.max(pad, vw - w - pad);
      if (left < pad) left = pad;
      setMenuRect({
        top: r.bottom + gap,
        left,
        width: w,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="mt-2 inline-flex w-full items-center justify-between gap-x-2 whitespace-nowrap rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-left text-sm text-[#111827] hover:border-[#d1d5db] hover:bg-[#fafafa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Payment method"
        onClick={() => setIsOpen((o) => !o)}
      >
        <span>{label}</span>
        <svg
          className="h-5 w-5 shrink-0 text-[#9ca3af]"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen &&
        menuRect != null &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[200] rounded-md border border-[#E5E7EB] bg-white shadow-lg focus:outline-none"
            style={{
              top: menuRect.top,
              left: menuRect.left,
              width: menuRect.width,
            }}
            role="menu"
            aria-orientation="vertical"
          >
            <div className="py-1" role="none">
              {PAYMENT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`block w-full px-4 py-2 text-left text-sm ${
                    value === opt.value
                      ? "bg-[#fef3ef] font-medium text-[#111827]"
                      : "text-[#374151] hover:bg-[#f9fafb]"
                  }`}
                  role="menuitem"
                  onClick={(e) => {
                    e.preventDefault();
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

const PaymentMethodSection = ({
  payment,
  cashInput,
  isCashCheckoutEnabled,
  cashCheckoutLoading,
  storeCorrelationId,
  customerOrderCorrelationId,
  onPaymentChange,
  onCashInputChange,
  onCashCheckoutClick,
  onCardPaymentSuccess,
}: PaymentMethodSectionProps) => {
  return (
    <div className="mt-0 flex h-full min-h-0 w-full flex-col">
      <h2 className="text-sm font-medium text-[#111827]">Select a payment method</h2>

      <PayBillPaymentMethodSelect value={payment} onChange={onPaymentChange} />

      {payment === PaymentMethodTypeEnum.CASH ? (
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
          <label htmlFor="paybill-cash-amount" className="text-sm font-medium text-[#374151]">
            Amount received
          </label>
          <div className="relative mt-2">
            <span
              className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-sm font-normal leading-none text-[#6B7280]"
              aria-hidden
            >
              $
            </span>
            <input
              id="paybill-cash-amount"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={cashInput}
              onChange={(e) => onCashInputChange(e.target.value)}
              className="box-border h-[42px] w-full rounded-[8px] border border-[#F1F1F2] bg-white pl-9 pr-4 text-sm font-normal leading-normal text-[#252525] placeholder:text-sm placeholder:font-normal placeholder:text-[#9CA3AF] focus:border-[#FD7657] focus:outline-none focus:ring-0 focus:ring-offset-0"
            />
          </div>

          <p className="mt-2 text-xs text-[#6B7280]">Enter the cash tendered using your keyboard.</p>

          <div className="mt-auto pt-6">
            <button
              type="button"
              onClick={onCashCheckoutClick}
              disabled={!isCashCheckoutEnabled || cashCheckoutLoading}
              className="flex w-full min-h-[44px] items-center justify-center rounded-lg bg-[#FD7657] py-3 text-sm font-medium text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-30"
            >
              {cashCheckoutLoading ? "Processing…" : "Pay now"}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {storeCorrelationId && customerOrderCorrelationId ? (
            <PayBillNmiPayment
              storeCorrelationId={storeCorrelationId}
              customerOrderCorrelationId={customerOrderCorrelationId}
              onSuccess={onCardPaymentSuccess}
            />
          ) : (
            <p className="mt-4 text-sm text-[#6B7280]">Missing order or store. Cannot load card payment.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSection;
